import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Maximize2, MoreHorizontal } from 'lucide-react';
import { moveTile, resizeTile } from '../data/commands/tiles';
import { useCommandEnv } from '../data/hooks';
import { cn } from '../ui/cn';
import { nextSize, SIZE_LABEL, sizeClass, type TileRow } from './layout';
import { getTileDef, type TileSize } from './registry';

const iconButton =
  'inline-flex min-h-11 min-w-11 items-center justify-center rounded-full text-muted disabled:opacity-40';

function EditableTile({ row, onMore }: { row: TileRow; onMore(id: string): void }) {
  const env = useCommandEnv();
  const def = getTileDef(row.type);
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: row.id,
  });
  if (!def) return null;
  const size = row.size as TileSize;
  return (
    <li
      ref={setNodeRef}
      data-testid="edit-tile"
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(sizeClass[size], isDragging && 'relative z-10 opacity-80')}
    >
      <div
        className={cn(
          'flex h-full min-h-[120px] flex-col gap-1 rounded-[24px] border border-dashed border-border bg-surface p-3',
          row.hidden && 'opacity-60',
        )}
      >
        <div className="flex items-center justify-between">
          <button
            type="button"
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            aria-label={`Drag to move ${def.title}`}
            className={cn(iconButton, 'touch-none')}
          >
            <GripVertical aria-hidden className="h-5 w-5" />
          </button>
          <div className="flex">
            <button
              type="button"
              aria-label={`Change size of ${def.title}, now ${size}`}
              disabled={def.sizes.length < 2}
              onClick={() => void resizeTile(env, row.id, nextSize(def, size))}
              className={iconButton}
            >
              <Maximize2 aria-hidden className="h-5 w-5" />
            </button>
            <button
              type="button"
              aria-label={`More for ${def.title}`}
              onClick={() => onMore(row.id)}
              className={iconButton}
            >
              <MoreHorizontal aria-hidden className="h-5 w-5" />
            </button>
          </div>
        </div>
        <p data-testid="edit-tile-title" className="font-medium">
          {def.title}
        </p>
        <p className="text-xs text-muted">
          {SIZE_LABEL[size]}
          {row.hidden ? ' · Hidden' : ''}
        </p>
      </div>
    </li>
  );
}

export function EditableGrid({ tiles, onMore }: { tiles: TileRow[]; onMore(id: string): void }) {
  const env = useCommandEnv();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const to = tiles.findIndex((r) => r.id === over.id);
    void moveTile(env, tiles, String(active.id), to);
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={tiles.map((r) => r.id)} strategy={rectSortingStrategy}>
        <ul
          aria-label="Edit Today tiles"
          className="grid grid-cols-2 gap-2.5 [grid-auto-flow:row_dense] md:grid-cols-4 md:gap-3"
        >
          {tiles.map((row) => (
            <EditableTile key={row.id} row={row} onMore={onMore} />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}
